import { filter, first } from 'rxjs/operators';
import { Component, OnInit, Input, OnDestroy, EventEmitter, Output, Inject } from '@angular/core';
import {
  RemoteControlService,
} from 'xsmodules/remote-control/services/remote-control.service';
import { RemoteControlVideoRecordingService, VideoRecordingStatus } from '../../services/video-recording.service';
import { DisplayMode } from '../toolbar-popover/toolbar-popover.component';
import { ClosePopoutTimerService } from '../../services/close-popout-timer.service';
import { BroadcastChannels, BroadcastService } from 'app/utils/broadcastService';
import { FeatureToggleService } from '@nx-fe/services';
import {
  WorkPanelBarItem,
  WorkPanelBarItemType,
} from 'xsmodules/ticket/components/work-panel/models/work-panel-bar-items';
import { XsI18nPipe } from '@nx-fe/pipes';
import { RcContext } from 'app/modules/remote-control-ng/context/rcContext';
import { RecordingState, RemoteControlDeviceState } from '../../services/device-state-registry.service';
// import { filter, first } from 'lodash';

@Component({
  selector: 'app-remote-control-video-recording-popover',
  templateUrl: './video-recording-popover.component.html',
  styleUrls: ['./video-recording-popover.component.scss'],
})
export class RemoteControlVideoRecordingPopoverComponent implements OnInit, OnDestroy {
  @Input() rcState: RemoteControlDeviceState;

  private displayMode = DisplayMode.ExpandCollapse;
  private readonly videoRecordingStatusChanged: BroadcastService<VideoRecordingStatus> = new BroadcastService(
    BroadcastChannels.onVideoRecordingStatusChanged
  );
  private closePopoutTimerService: ClosePopoutTimerService;
  private previousMonitorId = 0;

  public isMouseOver = false;
  public isSpinning = false;
  public isProcessignTooltipVisible = false;
  public isRecordingOrPause = false;
  public mouseOverAudio = false;

  public collapsedButtonItem: WorkPanelBarItem = {
    name: 'stopVideoCollapsedButton',
    type: WorkPanelBarItemType.Button,
    tooltip: () => this.saI18nPipe.transform('videoRecording_Stop_Recording'),
    onClick: () => {},
    cssClass: () => 'toolbar-popover-single-item',
    icon: () => 'hd-icon-stopVideo popover-button-stopVideo-red',
    isVisible: () => true,
    isDisabled: (): boolean => false,
  } as WorkPanelBarItem;

  public expandedButtonItems: WorkPanelBarItem[];

  constructor(
    private remoteControlVideoRecordingService: RemoteControlVideoRecordingService,
    private remoteControlService: RemoteControlService,
    private featureToggleService: FeatureToggleService,
    private saI18nPipe: XsI18nPipe
  ) {}

  ngOnInit() {
    // Subscribe to get changes on the videoRecordingStatus
    this.videoRecordingStatusChanged.onMessage = this.updateOnRecordingStatusChange;

    // Set the starting status to what ever the currently is
    this.handleRecordingState(this.remoteControlVideoRecordingService.recodingState);

    this.closePopoutTimerService = new ClosePopoutTimerService('.popover-container-box:hover', (): void => {
      this.onMouseLeave();
    });
  }

  ngOnDestroy(): void {
    if (this.videoRecordingStatusChanged) {
      this.videoRecordingStatusChanged.close();
    }

    this.closePopoutTimerService.clearTimer();
  }

  public get getDisplayMode() {
    return this.displayMode;
  }

  public get isNotRecordingOrPauseOrUploadingVideo(): boolean {
    return this.isSpinning || !this.isRecordingOrPause;
  }

  public get popOverOpen() {
    return this.isMouseOver && !this.isRecording && !this.isSpinning && !this.isVideoRecordingButtonDisabled();
  }

  public get expandedButtonsVisibleCount(): number {
    return this.expandedButtonItems?.filter(i => i.isVisible?.()).length || 0;
  }

  public get recordingWithAudio() {
    return this.isRecording && this.rcState.recordingWithAudio;
  }

  public get isRecording() {
    RcContext.instance.multiScreen.requestMonitorId$
      .pipe(
        filter(monitorId => monitorId !== undefined && monitorId != null && monitorId != this.previousMonitorId),
        first()
      )
      .subscribe(monitorId => {
        if (this.rcState && this.rcState.recording) {
          this.stopRecording();
        }
        this.previousMonitorId = monitorId;
      });
    return this.rcState && this.rcState.recording;
  }

  public get recordingPaused() {
    return this.rcState && this.rcState.recordingPaused;
  }

  public get recordingWithAudioEnabled() {
    return this.remoteControlVideoRecordingService.isVideoRecordingWithAudioEnabled;
  }

  public get videoRecordingClass(): string {
    return this.remoteControlVideoRecordingService.isVideoRecordingWithAudioEnabled && !this.isSpinning
      ? 'assist-mat-tooltip assist-mat-tooltip-space-extra'
      : 'assist-mat-tooltip assist-mat-tooltip-space';
  }

  public onMouseEnter() {
    this.isMouseOver = true;

    this.closePopoutTimerService.startTimer();
  }

  public startRecording = (withAudio: boolean) => {
    if (!this.isVideoRecordingButtonDisabled()) {
      this.isMouseOver = false;
      this.remoteControlVideoRecordingService.startVideoRecording(withAudio);
      this.buildRcMenuButtonItems('recording');
      this.isRecordingOrPause = this.remoteControlVideoRecordingService.recodingState !== RecordingState.NotRunning;
      this.isSpinning = !this.isRecordingOrPause;
    }
  };

  public pauseResumeRecording = () => {
    if (this.rcState.recordingPaused) {
      this.buildRcMenuButtonItems('recording');
      this.remoteControlVideoRecordingService.resumeVideoRecording();
    } else {
      this.buildRcMenuButtonItems('pause');
      this.remoteControlVideoRecordingService.pauseVideoRecording();
    }

    this.isRecordingOrPause = true;
  };

  public stopRecording = () => {
    this.remoteControlVideoRecordingService.stopVideoRecording();
    this.isRecordingOrPause = false;
  };

  private onMouseLeave = () => {
    this.isMouseOver = false;
  };

  private updateOnRecordingStatusChange = (status: VideoRecordingStatus) => {
    this.handleRecordingState(status.state);
  };

  public isVideoRecordingButtonDisabled = (): boolean =>
    this.remoteControlVideoRecordingService.recodingState !== RecordingState.NotRunning ||
    !this.remoteControlService.isRCContextReady();

  private handleRecordingState = (state: RecordingState): void => {
    this.isRecordingOrPause = state !== RecordingState.NotRunning;

    this.isSpinning =
      state === RecordingState.PendingAccept ||
      state === RecordingState.TranscodingVideo ||
      state === RecordingState.UploadingVideo;

    this.isProcessignTooltipVisible =
      state === RecordingState.TranscodingVideo || state === RecordingState.UploadingVideo;

    if (this.isRecordingOrPause && this.rcState) {
      const state = this.rcState.recordingPaused ? 'pause' : 'recording';
      this.buildRcMenuButtonItems(state);
    }
  };

  private buildRcMenuButtonItems = (recordingMode: string) => {
    let tooltip = '';
    let icon = '';
    let stopIcon = 'hd-icon-stopVideo';
    this.displayMode = DisplayMode.ExpandCollapse;

    switch (recordingMode) {
      case 'pause': {
        this.displayMode = DisplayMode.AlwaysOpen;
        tooltip = this.saI18nPipe.transform('videoRecording_Resume_Recording');
        icon = 'hd-icon-recordingVideo popover-button-recordingVideo-red';
        stopIcon += ' popover-button-stopVideo-black';
        break;
      }
      case 'recording': {
        tooltip = this.saI18nPipe.transform('videoRecording_Pause_Recording');
        icon = 'hd-icon-pauseVideo';
        stopIcon += ' popover-button-stopVideo-red';
        break;
      }
    }

    this.expandedButtonItems = [
      {
        name: 'stopVideoExpandedButton',
        type: WorkPanelBarItemType.Button,
        tooltip: () => this.saI18nPipe.transform('videoRecording_Stop_Recording'),
        onClick: () => {
          this.stopRecording();
        },
        icon: () => stopIcon,
        isVisible: () => true,
        isDisabled: () => false,
      } as WorkPanelBarItem,
      {
        name: recordingMode + 'ExpandedButton',
        type: WorkPanelBarItemType.Button,
        tooltip: () => tooltip,
        onClick: () => {
          this.pauseResumeRecording();
        },
        icon: () => icon,
        isVisible: () => true,
        isDisabled: () => false,
      } as WorkPanelBarItem,
    ];
  };
}
